import React from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Image from '@amzn/meridian/image';
import { useTranslation } from 'src/arb-wrapper';
import { BenderAssetType } from 'src/constants';
import { type CampaignDesiredStateDto } from 'src/store/generated/leela-api-generated';
import { sanitizeMeridianImageSrc } from 'src/utils';
import { useGetBenderTemplatesQuery } from 'src/store/bender-api';
import { VideoTemplateThumbnail } from 'src/components/campaigns/forms/TemplateSelectUtils';

interface ProductStrategyViewProps {
  strategy: CampaignDesiredStateDto['productStrategies'][0];
}

export const ProductStrategyView: React.FC<ProductStrategyViewProps> = ({ strategy }) => {
  const { t } = useTranslation();
  const { data: templates } = useGetBenderTemplatesQuery();

  // Prefer the editable strategy name; `name` is newer, so unnamed strategies fall back to
  // strategyId (then pageId) — matching buildStrategyLabelMap so the label is consistent everywhere.
  const strategyName = [strategy.name?.trim(), strategy.strategyId, strategy.pageId].find(Boolean);

  return (
    <Column spacing="300">
      {strategyName && (
        <Row spacing="200" widths={['240px', 'fill']}>
          <Text color="secondary" type="b300">{t('form_product_strategy_name')}</Text>
          <Text data-testid="product-strategy-name" type="b300">{strategyName}</Text>
        </Row>
      )}

      <Row spacing="200" widths={['240px', 'fill']}>
        <Text color="secondary" type="b300">{t('form_country')}</Text>
        <Text type="b300">{strategy.country}</Text>
      </Row>

      <Row spacing="200" widths={['240px', 'fill']}>
        <Text color="secondary" type="b300">{t('form_locales')}</Text>
        <Text type="b300">{strategy.locales?.join(', ')}</Text>
      </Row>

      {strategy.businessLine && (
        <Row spacing="200" widths={['240px', 'fill']}>
          <Text color="secondary" type="b300">{t('business_line_label')}</Text>
          <Text type="b300">{strategy.businessLine}</Text>
        </Row>
      )}

      {strategy.strategyId && (
        <Row spacing="200" widths={['240px', 'fill']}>
          <Text color="secondary" type="b300">{t('strategy_id_label')}</Text>
          <Text type="b300">{strategy.strategyId}</Text>
        </Row>
      )}

      {!strategy.strategyId && strategy.pageId && (
        <Row spacing="200" widths={['240px', 'fill']}>
          <Text color="secondary" type="b300">{t('page_id_label')}</Text>
          <Text type="b300">{strategy.pageId}</Text>
        </Row>
      )}

      {strategy.productSetName && (
        <Row spacing="200" widths={['240px', 'fill']}>
          <Text color="secondary" type="b300">{t('product_set_label')}</Text>
          <Text type="b300">{strategy.productSetName}</Text>
        </Row>
      )}

      {strategy.gtiList && strategy.gtiList.length > 0 && (
        <Row spacing="200" widths={['240px', 'fill']}>
          <Text color="secondary" type="b300">{t('form_gti_list_label')}</Text>
          <Text type="b300">{t('gti_count', { count: strategy.gtiList.length })}</Text>
        </Row>
      )}

      {strategy.benderTemplates && strategy.benderTemplates.length > 0 && (
        <Column spacing="200">
          <Text color="secondary" type="b300">{t('templates_label')}</Text>
          <Column spacing="200">
            {strategy.benderTemplates.map((templateConfig, templateIndex) => {
              const benderTemplate = templates?.find(t => t.id === templateConfig.templateId);
              return (
                <div key={templateIndex} style={{ padding: '20px', backgroundColor: '#ffffff', borderRadius: '6px', border: '1px solid #e0e0e0' }}>
                  <Row spacing="400">
                    <Column alignmentHorizontal="center" minWidth="140px">
                      {benderTemplate?.assetType === BenderAssetType.VIDEO
                        ? (
                        <VideoTemplateThumbnail
                          aspectRatio={benderTemplate.aspectRatio}
                          testId={`template-preview-video-${templateIndex}`}
                        />
                          )
                        : (
                        <div style={{ marginBottom: '0px', width: '100%' }}>
                          <Image
                            className="campaigns-table-template-preview"
                            data-testid={`template-preview-${templateIndex}`}
                            size="contain"
                            src={sanitizeMeridianImageSrc(benderTemplate?.previewImageUrl ?? '')}
                          />
                        </div>
                          )}
                    </Column>
                    <Column spacing="200">
                      <Row spacing="200" widths={['160px', 'fill']}>
                        <Text color="secondary" type="b300">{t('template_name_label')}</Text>
                        <Text type="b300">{benderTemplate?.title ?? templateConfig.templateId}</Text>
                      </Row>

                      {benderTemplate?.description && (
                        <Row spacing="200" widths={['160px', 'fill']}>
                          <Text color="secondary" type="b300">{t('description_label')}</Text>
                          <Text type="b300">{benderTemplate.description}</Text>
                        </Row>
                      )}

                      <Row spacing="200" widths={['160px', 'fill']}>
                        <Text color="secondary" type="b300">{t('asset_type_label')}</Text>
                        <Text type="b300">{templateConfig.assetType}</Text>
                      </Row>

                      {benderTemplate && (
                        <Row spacing="200" widths={['160px', 'fill']}>
                          <Text color="secondary" type="b300">{t('aspect_ratio_label')}</Text>
                          <Text type="b300">{benderTemplate.aspectRatio}</Text>
                        </Row>
                      )}

                      <Row spacing="200" widths={['160px', 'fill']}>
                        <Text color="secondary" type="b300">{t('template_id_label')}</Text>
                        <Text color="secondary" type="b300">{templateConfig.templateId}</Text>
                      </Row>
                    </Column>
                  </Row>
                </div>
              );
            })}
          </Column>
        </Column>
      )}
    </Column>
  );
};
